// React Query hooks for the knowledge base (routes/knowledge/index.ts). All
// keys live under ['kb', workspaceId, …]; every mutation invalidates that
// whole namespace — pages, trees, history and proposals hang together
// (a move changes trees and pages, an approval changes a page and the
// proposal list) and the data is small.

import { useMemo } from 'react'
import {
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import type { KbFolderPolicy, KbRevisionStatus } from '@goblic/shared'
import { formatKbSpace } from '@goblic/shared'
import { ApiError, kbApi, projectsApi } from '../lib/api-client'
import { useAuth, useWorkspaceId } from '../lib/auth'

const STALE_MS = 15_000

export function kbKey(workspaceId: string, ...rest: unknown[]): unknown[] {
  return ['kb', workspaceId, ...rest]
}

/**
 * What the signed-in user may do (permission-resolver.service.ts):
 * reading = any member; `kb.write` = operator, administrator, owner;
 * `kb.review` = approver, administrator, owner. The API stays authoritative —
 * this only decides which buttons to show.
 */
export function useKbPermissions(): { canWrite: boolean; canReview: boolean } {
  const roles = useAuth().user?.roles ?? []
  const has = (...names: string[]) => roles.some((role) => names.includes(role))
  return {
    canWrite: has('workspace_owner', 'administrator', 'operator'),
    canReview: has('workspace_owner', 'administrator', 'approver'),
  }
}

export interface KbSpaceInfo {
  space: string
  label: string
  projectId: string | null
}

/**
 * The spaces to show: the workspace plus every project of the workspace
 * ("project access" = the project belongs to the workspace, contract §8).
 * With `projectId`, only that project's space.
 */
export function useKbSpaces(projectId?: string | null): {
  spaces: KbSpaceInfo[]
  isLoading: boolean
  labelOf: (space: string) => string
} {
  const workspaceId = useWorkspaceId()
  // Every project (paged through), so no space goes missing past the first page.
  const projectsQuery = useQuery({
    queryKey: ['projects', workspaceId, 'all'],
    queryFn: () => projectsApi.listAll({ workspaceId }),
    enabled: workspaceId !== '',
    staleTime: 60_000,
  })
  const projects = projectsQuery.data
  return useMemo(() => {
    const all: KbSpaceInfo[] = [
      { space: 'workspace', label: 'Workspace', projectId: null },
      ...(projects ?? []).map((project) => ({
        space: formatKbSpace({ kind: 'project', projectId: project.id }),
        label: project.name,
        projectId: project.id,
      })),
    ]
    const spaces = projectId
      ? all.filter((info) => info.projectId === projectId)
      : all
    const labelOf = (space: string) =>
      all.find((info) => info.space === space)?.label ??
      (space === 'workspace' ? 'Workspace' : 'Project')
    return {
      spaces:
        projectId && spaces.length === 0
          ? [{ space: `project:${projectId}`, label: 'Project', projectId }]
          : spaces,
      isLoading: projectsQuery.isLoading,
      labelOf,
    }
  }, [projects, projectId, projectsQuery.isLoading])
}

/**
 * Trees of the given spaces. These GETs never seed: an overview of every
 * space must not write starter pages into each of them (contract §2).
 * `seedSpace` is the one space the user explicitly opened: when its tree
 * comes back empty and the user may write, it is fetched once more with
 * `?seed=true` so the API writes the starter pages (and the tree shows them).
 */
export function useKbTrees(spaces: string[], options: { seedSpace?: string | null } = {}) {
  const workspaceId = useWorkspaceId()
  const queryClient = useQueryClient()
  const { canWrite } = useKbPermissions()
  const results = useQueries({
    queries: spaces.map((space) => ({
      queryKey: kbKey(workspaceId, 'tree', space),
      queryFn: () => kbApi.tree(workspaceId, space),
      enabled: workspaceId !== '',
      staleTime: STALE_MS,
    })),
  })
  const seedSpace = options.seedSpace ?? null
  const seedIndex = seedSpace ? spaces.indexOf(seedSpace) : -1
  const seedTree = seedIndex >= 0 ? results[seedIndex]?.data : undefined
  useQuery({
    queryKey: kbKey(workspaceId, 'seed', seedSpace),
    queryFn: async () => {
      const tree = await kbApi.tree(workspaceId, seedSpace!, { seed: true })
      queryClient.setQueryData(kbKey(workspaceId, 'tree', seedSpace), tree)
      return tree
    },
    enabled:
      workspaceId !== '' &&
      canWrite &&
      seedSpace !== null &&
      seedTree !== undefined &&
      seedTree.entries.length === 0,
    // Once per space per session is enough; the API only seeds a never-used space.
    staleTime: Infinity,
    retry: false,
  })
  return results
}

export function useKbPoliciesFor(spaces: string[]) {
  const workspaceId = useWorkspaceId()
  return useQueries({
    queries: spaces.map((space) => ({
      queryKey: kbKey(workspaceId, 'policies', space),
      queryFn: () => kbApi.policies(workspaceId, space),
      enabled: workspaceId !== '',
      staleTime: 60_000,
    })),
  })
}

export function useKbPage(
  space: string | null,
  path: string | null,
  options: { includeDeleted?: boolean } = {}
) {
  const workspaceId = useWorkspaceId()
  return useQuery({
    queryKey: kbKey(workspaceId, 'page', space, path, Boolean(options.includeDeleted)),
    queryFn: () =>
      kbApi.getPage(workspaceId, space!, path!, {
        includeDeleted: options.includeDeleted,
      }),
    enabled: workspaceId !== '' && !!space && !!path,
    staleTime: STALE_MS,
    // A missing page is an answer, not a hiccup.
    retry: (count, error) => !(error instanceof ApiError && error.status === 404) && count < 2,
  })
}

export function useKbRevisions(space: string | null, path: string | null) {
  const workspaceId = useWorkspaceId()
  return useQuery({
    queryKey: kbKey(workspaceId, 'revisions', space, path),
    queryFn: () => kbApi.revisions(workspaceId, space!, path!),
    enabled: workspaceId !== '' && !!space && !!path,
    staleTime: STALE_MS,
  })
}

/** Full revisions (text included) for the given ids, e.g. the two being compared. */
export function useKbRevisionTexts(space: string | null, revisionIds: string[]) {
  const workspaceId = useWorkspaceId()
  const results = useQueries({
    queries: revisionIds.map((id) => ({
      queryKey: kbKey(workspaceId, 'revision', space, id),
      queryFn: () => kbApi.revision(workspaceId, space!, id),
      enabled: workspaceId !== '' && !!space,
      // A revision never changes.
      staleTime: Infinity,
    })),
  })
  const raws: Record<string, string | undefined> = {}
  results.forEach((result, index) => {
    if (result.data) raws[revisionIds[index]!] = result.data.raw
  })
  return raws
}

export function useKbSearch(query: string, space?: string) {
  const workspaceId = useWorkspaceId()
  const q = query.trim()
  return useQuery({
    queryKey: kbKey(workspaceId, 'search', q, space ?? null),
    queryFn: () => kbApi.search(workspaceId, { q, space, limit: 20 }),
    enabled: workspaceId !== '' && q.length > 0,
    staleTime: STALE_MS,
  })
}

/** Pending (by default) proposals; only fetched for reviewers. */
export function useKbProposals(
  options: { space?: string; status?: KbRevisionStatus; enabled?: boolean } = {}
) {
  const workspaceId = useWorkspaceId()
  const { canReview } = useKbPermissions()
  return useQuery({
    queryKey: kbKey(workspaceId, 'proposals', options.status ?? 'proposed', options.space ?? null),
    queryFn: () =>
      kbApi.proposals(workspaceId, {
        status: options.status ?? 'proposed',
        space: options.space,
      }),
    enabled: workspaceId !== '' && canReview && options.enabled !== false,
    staleTime: STALE_MS,
    refetchInterval: 60_000,
  })
}

export function useKbRunActivity(runId: string, options: { live?: boolean } = {}) {
  const workspaceId = useWorkspaceId()
  return useQuery({
    queryKey: kbKey(workspaceId, 'run-activity', runId),
    queryFn: () => kbApi.runActivity(workspaceId, runId),
    enabled: workspaceId !== '' && runId !== '',
    refetchInterval: options.live ? 10_000 : false,
  })
}

/** Every knowledge-base write, each invalidating the ['kb', workspaceId] namespace. */
export function useKbMutations() {
  const workspaceId = useWorkspaceId()
  const queryClient = useQueryClient()
  const onSuccess = () =>
    queryClient.invalidateQueries({ queryKey: kbKey(workspaceId) })

  return {
    savePage: useMutation({
      mutationFn: (input: {
        space: string
        path: string
        content: string
        message?: string
        baseRev?: number
      }) =>
        kbApi.putPage(workspaceId, input.space, input.path, {
          content: input.content,
          message: input.message,
          baseRev: input.baseRev,
        }),
      onSuccess,
    }),
    deletePage: useMutation({
      mutationFn: (input: { space: string; path: string; message?: string }) =>
        kbApi.deletePage(workspaceId, input.space, input.path, input.message),
      onSuccess,
    }),
    move: useMutation({
      mutationFn: (input: { space: string; from: string; to: string }) =>
        kbApi.move(workspaceId, input.space, { from: input.from, to: input.to }),
      onSuccess,
    }),
    verify: useMutation({
      mutationFn: (input: { space: string; path: string }) =>
        kbApi.verify(workspaceId, input.space, input.path),
      onSuccess,
    }),
    restoreRevision: useMutation({
      mutationFn: (input: { space: string; revisionId: string; message?: string }) =>
        kbApi.restoreRevision(workspaceId, input.space, input.revisionId, input.message),
      onSuccess,
    }),
    putPolicies: useMutation({
      mutationFn: (input: { space: string; policies: KbFolderPolicy[] }) =>
        kbApi.putPolicies(workspaceId, input.space, input.policies),
      onSuccess,
    }),
    approveProposal: useMutation({
      mutationFn: (input: { revisionId: string; note?: string; force?: boolean }) =>
        kbApi.approveProposal(workspaceId, input.revisionId, {
          note: input.note,
          force: input.force,
        }),
      onSuccess,
    }),
    rejectProposal: useMutation({
      mutationFn: (input: { revisionId: string; note?: string }) =>
        kbApi.rejectProposal(workspaceId, input.revisionId, { note: input.note }),
      onSuccess,
    }),
  }
}
